import { filter, flow, get, trim } from 'lodash/fp';
import {
    type FinanceProductSettings,
    FinanceProductType,
    type ResidualValueTableCellForVehicleParameterPayload,
    type InsuranceFeeTableValuesForVehicleParameterPayload,
    VehicleKind,
    type BalloonValueTableCellForVehicleParameterFilterPayload,
    type LeaseValueTableCellForVehicleParameterFilterPayload,
} from '../../../../../api/types';
import { type FinanceProductFormValues } from '../index';
import {
    finderVehicleParameterKey,
    variantParameterKey,
} from '../tables/LeasePurchaseTable/useVehicleReferenceParameterRecords';
import { type RecordType, hasVehicle } from '../VehicleAssignmentTab/useVehicles';

const mapFormValuesToPayload = (
    formValues: FinanceProductFormValues,
    { vehicles = [], finderVehicles = [] }: { vehicles?: RecordType[]; finderVehicles?: RecordType[] }
): FinanceProductSettings => {
    // get list variant references
    const variantReferences: string = get('vehicleReferenceParameters.finderVehicle.variants', formValues) || '';

    // clean data by trimming values
    // and removing empty spaces
    const variants = flow([variants => variants.map(variant => trim(variant)), filter(Boolean)])(
        variantReferences.split(',')
    );

    const hasAssignedVehicle = formValues.variantSuiteIds.some(id => hasVehicle(vehicles, id));
    const hasAssignedFinderVehicle = formValues.variantSuiteIds.some(id => hasVehicle(finderVehicles, id));

    const payload = {
        ...formValues,
        vehicleReferenceParameters: {
            variant: {
                ...formValues.vehicleReferenceParameters.variant,
                isAddOnSpecificVehicles: hasAssignedVehicle,
            },
            finderVehicle: {
                ...formValues.vehicleReferenceParameters.finderVehicle,
                variants,
                isAddOnSpecificVehicles: hasAssignedFinderVehicle,
            },
        },
    };

    if (formValues.type === FinanceProductType.LeasePurchase) {
        const { table } = formValues.residualValue;

        const filteredTable =
            table.filter(({ variantSuiteId }) => formValues.variantSuiteIds.includes(variantSuiteId)) || [];
        const vehicleFilterParameterTable: ResidualValueTableCellForVehicleParameterPayload[] =
            table
                .filter(({ variantSuiteId }) =>
                    [variantParameterKey, finderVehicleParameterKey].includes(variantSuiteId)
                )
                .map(({ variantSuiteId, ...values }) => ({
                    ...values,
                    type: variantSuiteId === variantParameterKey ? VehicleKind.LocalVariant : VehicleKind.FinderVehicle,
                })) || [];

        payload.residualValue = {
            ...payload.residualValue,
            table: filteredTable,
            vehicleFilterParameterTable,
        };
    }

    if (formValues.type === FinanceProductType.UcclLeasing) {
        const { table } = formValues.insuranceFee;

        const filteredTable =
            table.filter(({ variantSuiteId }) => formValues.variantSuiteIds.includes(variantSuiteId)) || [];

        const vehicleFilterParameterTable: InsuranceFeeTableValuesForVehicleParameterPayload[] =
            table
                .filter(({ variantSuiteId }) =>
                    [variantParameterKey, finderVehicleParameterKey].includes(variantSuiteId)
                )
                .map(({ variantSuiteId, ...values }) => ({
                    ...values,
                    type: variantSuiteId === variantParameterKey ? VehicleKind.LocalVariant : VehicleKind.FinderVehicle,
                })) || [];

        payload.insuranceFee = {
            ...payload.insuranceFee,
            table: filteredTable,
            vehicleFilterParameterTable,
        };
    }

    if (formValues.type === FinanceProductType.HirePurchaseWithBalloon) {
        const { table, type } = formValues.balloon;

        if (type !== 'table') {
            return payload;
        }

        const filteredTable =
            table.filter(({ variantSuiteId }) => formValues.variantSuiteIds.includes(variantSuiteId)) || [];
        const vehicleFilterParameterTable: BalloonValueTableCellForVehicleParameterFilterPayload[] =
            table
                .filter(({ variantSuiteId }) =>
                    [variantParameterKey, finderVehicleParameterKey].includes(variantSuiteId)
                )
                .map(({ variantSuiteId, ...values }) => ({
                    ...values,
                    type: variantSuiteId === variantParameterKey ? VehicleKind.LocalVariant : VehicleKind.FinderVehicle,
                })) || [];

        payload.balloon = {
            ...payload.balloon,
            table: filteredTable,
            vehicleFilterParameterTable,
        };
    }

    if (formValues.type === FinanceProductType.HirePurchaseWithBalloonGfv) {
        const { table } = formValues.balloonGfv;

        const filteredTable =
            table.filter(({ variantSuiteId }) => formValues.variantSuiteIds.includes(variantSuiteId)) || [];
        const vehicleFilterParameterTable: BalloonValueTableCellForVehicleParameterFilterPayload[] =
            table
                .filter(({ variantSuiteId }) =>
                    [variantParameterKey, finderVehicleParameterKey].includes(variantSuiteId)
                )
                .map(({ variantSuiteId, ...values }) => ({
                    ...values,
                    type: variantSuiteId === variantParameterKey ? VehicleKind.LocalVariant : VehicleKind.FinderVehicle,
                })) || [];

        payload.balloonGfv = {
            ...payload.balloonGfv,
            table: filteredTable,
            vehicleFilterParameterTable,
        };
    }

    if (formValues.type === FinanceProductType.Lease) {
        const { table } = formValues.lease;

        const filteredTable =
            table.filter(({ variantSuiteId }) => formValues.variantSuiteIds.includes(variantSuiteId)) || [];
        const vehicleFilterParameterTable: LeaseValueTableCellForVehicleParameterFilterPayload[] =
            table
                .filter(({ variantSuiteId }) =>
                    [variantParameterKey, finderVehicleParameterKey].includes(variantSuiteId)
                )
                .map(({ variantSuiteId, ...values }) => ({
                    ...values,
                    type: variantSuiteId === variantParameterKey ? VehicleKind.LocalVariant : VehicleKind.FinderVehicle,
                })) || [];

        payload.lease = {
            ...payload.lease,
            table: filteredTable,
            vehicleFilterParameterTable,
        };
    }

    return payload;
};

export default mapFormValuesToPayload;
